/**
 * Operator activity: every sign-in, refused sign-in and action on the
 * platform, newest first, kept for good. Written by the server in the same
 * transaction as each action; nothing here can change it, and nothing on the
 * server can either.
 *
 * `?restaurant=<id>` narrows it to one restaurant (the restaurant page links
 * here that way).
 */

import { Link, useSearchParams } from 'react-router-dom';
import { displayLocale, usePlatformActivity } from '@restaurant/api-client';
import { Button, Card, Empty, ErrorNotice, Loading } from '@restaurant/ui';

import { byDay, dayHeading, formatClock, localDay } from '../words';

export function ActivityPage() {
  const [params, setParams] = useSearchParams();
  const restaurantId = params.get('restaurant');
  const log = usePlatformActivity(restaurantId);
  const locale = displayLocale();
  const entries = log.data?.pages.flatMap((page) => page.entries) ?? [];
  const today = localDay(new Date().toISOString());

  return (
    <>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">Operator activity</h1>
          <p className="pf__sub">
            Everything done on the platform admin, and every sign-in to it, newest first.
          </p>
        </div>
        {restaurantId && <Button onClick={() => setParams({})}>Show every restaurant</Button>}
      </header>

      <Card>
        {log.isPending && <Loading label="Loading" />}
        {log.isError && <ErrorNotice message={log.error.message} />}
        {log.data && entries.length === 0 && <Empty title="Nothing recorded yet" />}
        {entries.length > 0 && (
          <div className="pf__days">
            {byDay(entries).map(([day, rows]) => (
              <section key={day} className="pf__day">
                <h2 className="pf__day-title">{dayHeading(day, today, locale)}</h2>
                <ol className="pf__log">
                  {rows.map((entry) => (
                    <li key={entry.id} className="pf__log-row">
                      <time className="pf__log-time" dateTime={entry.occurred_at}>
                        {formatClock(entry.occurred_at, locale)}
                      </time>
                      <div className="pf__log-body">
                        <p className="pf__log-what">{entry.summary}</p>
                        <p className="pf__log-meta">
                          <span>{entry.operator_email}</span>
                          {entry.restaurant_id && (
                            <Link
                              className="pf__rowlink"
                              to={`/restaurants/${entry.restaurant_id}`}
                            >
                              {entry.restaurant_name ?? 'A restaurant'}
                            </Link>
                          )}
                          {entry.address && <span>from {entry.address}</span>}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
        )}
        {log.hasNextPage && (
          <Button loading={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
            Show older
          </Button>
        )}
      </Card>
    </>
  );
}
